import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from './Icon';
import styles from './ui.module.css';

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ');

// --- Button ------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'lg';
  block?: boolean;
  iconOnly?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  block = false,
  iconOnly = false,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        styles.button,
        styles[variant],
        size === 'lg' && styles.large,
        block && styles.block,
        iconOnly && styles.iconOnly,
        className,
      )}
      {...rest}
    />
  );
}

export function IconButton({
  name,
  label,
  iconSize = 20,
  ...rest
}: Omit<ButtonProps, 'children' | 'iconOnly'> & {
  name: IconName;
  label: string;
  iconSize?: number;
}) {
  return (
    <Button iconOnly variant={rest.variant ?? 'ghost'} aria-label={label} title={label} {...rest}>
      <Icon name={name} size={iconSize} />
    </Button>
  );
}

// --- Chip --------------------------------------------------------------------

export function Chip({
  selected = false,
  count,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean; count?: number }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cx(styles.chip, selected && styles.chipSelected, className)}
      {...rest}
    >
      {children}
      {count !== undefined && <span className={styles.chipCount}>{count}</span>}
    </button>
  );
}

// --- Segmented control -------------------------------------------------------

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange(value: T): void;
  label: string;
}) {
  return (
    <div className={styles.segmented} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          className={cx(styles.segment, option.value === value && styles.segmentSelected)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// --- Fields ------------------------------------------------------------------

export function Field({
  label,
  optional,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  optional?: string;
  hint?: string;
  error?: string | null;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.field}>
      <label className={cx(styles.fieldLabel, 'eyebrow')} htmlFor={htmlFor}>
        <span>{label}</span>
        {optional && <span className={styles.fieldOptional}>{optional}</span>}
      </label>
      {children}
      {error ? (
        <p className={styles.fieldError}>{error}</p>
      ) : hint ? (
        <p className={styles.fieldHint}>{hint}</p>
      ) : null}
    </div>
  );
}

export function TextInput({
  className,
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input ref={ref} className={cx(styles.input, className)} {...rest} />;
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(styles.textarea, className)} {...rest} />;
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx(styles.select, className)} {...rest} />;
}

export function SearchInput({
  value,
  onValueChange,
  placeholder,
  label,
  clearLabel,
  inputRef,
}: {
  value: string;
  onValueChange(value: string): void;
  placeholder: string;
  label: string;
  clearLabel: string;
  inputRef?: Ref<HTMLInputElement>;
}) {
  return (
    <div className={styles.searchWrap}>
      <Icon name="search" size={18} className={styles.searchIcon} />
      <input
        ref={inputRef}
        type="search"
        inputMode="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        className={styles.searchInput}
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
      />
      {value && (
        <button
          type="button"
          className={styles.searchClear}
          aria-label={clearLabel}
          onClick={() => onValueChange('')}
        >
          <Icon name="close" size={18} />
        </button>
      )}
    </div>
  );
}

// --- Switch ------------------------------------------------------------------

export function Switch({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange(checked: boolean): void;
  label: string;
  hint?: string;
}) {
  return (
    <label className={styles.switchRow}>
      <span className={styles.switchText}>
        <span className={styles.switchLabel}>{label}</span>
        {hint && <span className={styles.switchHint}>{hint}</span>}
      </span>
      <input
        type="checkbox"
        className={styles.switchInput}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.switchTrack} aria-hidden="true">
        <span className={styles.switchThumb} />
      </span>
    </label>
  );
}

// --- Stars -------------------------------------------------------------------

export function StarRating({
  value,
  onChange,
  label,
  starLabel,
}: {
  value: number | null;
  onChange(value: number | null): void;
  label: string;
  starLabel(count: number): string;
}) {
  return (
    <div className={styles.stars} role="group" aria-label={label}>
      {[1, 2, 3, 4, 5].map((star) => {
        const on = value !== null && star <= value;
        return (
          <button
            key={star}
            type="button"
            aria-label={starLabel(star)}
            aria-pressed={on}
            className={cx(styles.star, on && styles.starOn)}
            // Tapping the current rating clears it — no separate reset control.
            onClick={() => onChange(value === star ? null : star)}
          >
            <Icon name="star" size={26} filled={on} />
          </button>
        );
      })}
    </div>
  );
}

export function StarsReadonly({ value, label }: { value: number; label: string }) {
  return (
    <span className={cx(styles.stars, styles.starsReadonly)} aria-label={label} title={label}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Icon key={star} name="star" size={13} filled={star <= value} />
      ))}
    </span>
  );
}

// --- Empty state -------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <Icon name={icon} size={22} />
      </div>
      <h2 className={styles.emptyTitle}>{title}</h2>
      {body && <p className={styles.emptyBody}>{body}</p>}
      {action}
    </div>
  );
}
